import { useState, useEffect, useCallback, useRef } from 'react';
import { createAgentMessage } from '../utils';

const PAGE_SIZE = 30;

/**
 * Hook para gestionar el historial de mensajes, paginación con scroll infinito
 * y el envío optimista de mensajes de texto y archivos multimedia (WhatsApp).
 */
export const useTicketMessages = ({
  activeTicketId,
  ticketPhone,
  token,
  idUsuario,
  nombreCompleto,
  inputText,
  setInputText,
  selectedFile,
  setSelectedFile,
  filePreview,
  setFilePreview,
  syncTicketAssignmentAfterMessage,
  setTicketDetail,
  onTicketUpdated,
  showErrorToast
}) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);

  const showErrorToastRef = useRef(showErrorToast);
  showErrorToastRef.current = showErrorToast;

  // 1. Cargar historial inicial de mensajes del ticket
  useEffect(() => {
    if (!activeTicketId || activeTicketId === 'undefined' || !token) return;

    const fetchMessages = async () => {
      setIsLoading(true);
      setError(null);
      setHasMore(true);

      try {
        const response = await fetch(`/api/SupportEngine/GetTicketMessages/${activeTicketId}?pageSize=${PAGE_SIZE}`, {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
        });

        if (response.ok) {
          const result = await response.json();
          const msgs = result.data || result;
          if (Array.isArray(msgs)) {
            const chronological = msgs.slice().reverse();
            setMessages(chronological);
            if (msgs.length < PAGE_SIZE) {
              setHasMore(false);
            }
          }
        } else {
          const errMsg = `Error al cargar mensajes (${response.status})`;
          setError(errMsg);
          if (showErrorToastRef.current) showErrorToastRef.current(errMsg);
        }
      } catch (err) {
        const errMsg = `Error de red: ${err.message}`;
        setError(errMsg);
        if (showErrorToastRef.current) showErrorToastRef.current(errMsg);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();
  }, [activeTicketId, token]);

  // 2. Cargar mensajes más antiguos al scrollear hacia arriba
  const handleLoadMore = useCallback(async () => {
    if (!activeTicketId || activeTicketId === 'undefined' || isLoadingMore || !hasMore || messages.length === 0 || !token) return;

    const oldestMessage = messages[0];
    const cursorId = oldestMessage?.idMensaje;
    if (!cursorId) return;

    setIsLoadingMore(true);
    try {
      const msgsRes = await fetch(`/api/SupportEngine/GetTicketMessages/${activeTicketId}?cursor=${cursorId}&pageSize=${PAGE_SIZE}`, {
        headers: { Accept: 'application/json', Authorization: 'Bearer ' + token }
      });

      if (msgsRes.ok) {
        const result = await msgsRes.json();
        const olderMsgs = result.data || result;
        if (Array.isArray(olderMsgs) && olderMsgs.length > 0) {
          const chronologicalOlder = olderMsgs.slice().reverse();
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.idMensaje));
            const filteredNew = chronologicalOlder.filter(m => !existingIds.has(m.idMensaje));
            return [...filteredNew, ...prev];
          });
          if (olderMsgs.length < PAGE_SIZE) {
            setHasMore(false);
          }
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      console.error('Error al cargar mensajes anteriores:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [activeTicketId, isLoadingMore, hasMore, messages, token]);

  // 3. Envío unificado de mensajes (texto o archivo multimedia)
  const handleSendMessage = useCallback(async (e, textOverride = null, fileOverride = null, previewOverride = null) => {
    if (e) e.preventDefault();
    const messageText = (textOverride !== null ? textOverride : inputText).trim();
    const fileToSend = fileOverride || selectedFile;
    const previewToSend = previewOverride || filePreview;
    if ((!messageText && !fileToSend) || isSending) return;

    setInputText('');
    setSelectedFile(null);
    setFilePreview(null);
    setIsSending(true);

    const currentReply = replyingTo;
    setReplyingTo(null);

    // ─── Caso A: Envío de archivo multimedia ─────────────────────────────────
    if (fileToSend) {
      const isImg = fileToSend.type.startsWith('image/');
      const isVid = fileToSend.type.startsWith('video/');
      const previewUrl = previewToSend?.url || (fileToSend instanceof File ? URL.createObjectURL(fileToSend) : null);
      const tempId = Date.now();
      const tempMediaMsg = {
        idMensaje: tempId,
        isTemp: true,
        bEsAgente: 1,
        idCatTipoMensaje: isImg ? 2 : (isVid ? 5 : 6),
        vTextoMensaje: messageText,
        vMediaUrl: previewUrl,
        vMediaMimeType: fileToSend.type,
        vCreatedUser: nombreCompleto || 'Tú',
        dCreatedDate: new Date().toISOString(),
        vContextWaMessageId: currentReply?.vWaMessageId || null,
        vQuotedText: currentReply?.vTextoMensaje || currentReply?.text || null,
        vQuotedAuthor: currentReply?.vCreatedUser || currentReply?.author || (currentReply?.bEsAgente ? 'Asesor' : 'Cliente'),
        bQuotedEsAgente: currentReply?.bEsAgente !== undefined ? currentReply.bEsAgente : (currentReply?.isAgent ? 1 : 0),
        idQuotedTipoMensaje: currentReply?.idCatTipoMensaje || null
      };

      // 1. Mostrar la imagen INMEDIATAMENTE en el chat (con su vista previa local)
      setMessages(prev => [...prev, tempMediaMsg]);

      try {
        const formData = new FormData();
        formData.append('file', fileToSend);
        formData.append('idTicket', activeTicketId);
        if (ticketPhone) {
          formData.append('toPhoneNumber', ticketPhone);
        }
        if (messageText) {
          formData.append('caption', messageText);
        }
        if (idUsuario) {
          formData.append('idUsuario', idUsuario);
        }
        if (currentReply?.vWaMessageId) {
          formData.append('contextWaMessageId', currentReply.vWaMessageId);
        }

        const response = await fetch('/api/SupportEngine/SendTicketMedia', {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token
          },
          body: formData
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          let errMsg = errData.message || 'Error al enviar archivo multimedia por WhatsApp.';
          if (errData.details) {
            try {
              const parsed = typeof errData.details === 'string' ? JSON.parse(errData.details) : errData.details;
              if (parsed?.error?.error_data?.details) {
                errMsg = parsed.error.error_data.details;
              } else if (parsed?.message) {
                errMsg = parsed.message;
              }
            } catch {
              // Mantener errMsg por defecto si falla el parseo de details
            }
          }
          if (showErrorToast) showErrorToast(errMsg);
          setMessages(prev => prev.filter(m => m.idMensaje !== tempId));
        } else {
          const result = await response.json();
          const newMsg = result.data || result;
          if (newMsg && (newMsg.idMensaje || newMsg.id)) {
            setMessages(prev => {
              const mergedMsg = {
                ...newMsg,
                vMediaUrl: newMsg.vMediaUrl || previewUrl,
                isTemp: false
              };
              const alreadyHasReal = prev.some(m => m.idMensaje === newMsg.idMensaje);
              if (alreadyHasReal) {
                return prev.filter(m => m.idMensaje !== tempId).map(m => m.idMensaje === newMsg.idMensaje ? { ...m, vMediaUrl: m.vMediaUrl || previewUrl, isTemp: false } : m);
              }
              return prev.map(m => m.idMensaje === tempId ? mergedMsg : m);
            });
          }

          if (syncTicketAssignmentAfterMessage) {
            await syncTicketAssignmentAfterMessage();
          }

          if (setTicketDetail) {
            setTicketDetail(prev => prev ? { ...prev, dUltimaActividad: new Date().toISOString() } : prev);
          }

          if (onTicketUpdated) onTicketUpdated();

          window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
            detail: {
              idTicket: parseInt(activeTicketId),
              vTextoMensaje: messageText || 'Archivo multimedia',
              dUltimaActividad: new Date().toISOString(),
              bEsAgente: 1
            }
          }));

          window.dispatchEvent(new CustomEvent('akasia:support:ticket_read', {
            detail: { idTicket: parseInt(activeTicketId) }
          }));
        }
      } catch (err) {
        if (showErrorToast) showErrorToast('Error de red al enviar archivo: ' + err.message);
        setMessages(prev => prev.filter(m => m.idMensaje !== tempId));
      } finally {
        setIsSending(false);
      }
      return;
    }

    // ─── Caso B: Envío de mensaje de texto tradicional ──────────────────────
    const tempMsg = {
      ...createAgentMessage(messageText, nombreCompleto),
      isTemp: true,
      vContextWaMessageId: currentReply?.vWaMessageId || null,
      vQuotedText: currentReply?.vTextoMensaje || currentReply?.text || null,
      vQuotedAuthor: currentReply?.vCreatedUser || currentReply?.author || (currentReply?.bEsAgente ? 'Asesor' : 'Cliente'),
      bQuotedEsAgente: currentReply?.bEsAgente !== undefined ? currentReply.bEsAgente : (currentReply?.isAgent ? 1 : 0),
      idQuotedTipoMensaje: currentReply?.idCatTipoMensaje || null
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const response = await fetch('/api/SupportEngine/SendTicketMessage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(activeTicketId),
          toPhoneNumber: ticketPhone,
          message: messageText,
          idUsuario: idUsuario ? Number(idUsuario) : null,
          contextWaMessageId: currentReply?.vWaMessageId || null
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        let errMsg = errData.message || errData.title;
        if (errData.errors) {
          errMsg = Object.values(errData.errors).flat().join(', ');
        }
        if (showErrorToast) showErrorToast(errMsg || 'Error al enviar el mensaje por WhatsApp.');
        setMessages(prev => prev.filter(m => m.idMensaje !== tempMsg.idMensaje));
      } else {
        const result = await response.json().catch(() => ({}));
        if (result.data) {
          setMessages(prev => {
            const alreadyHasReal = prev.some(m => m.idMensaje === result.data.idMensaje);
            if (alreadyHasReal) {
              return prev.filter(m => m.idMensaje !== tempMsg.idMensaje);
            }
            return prev.map(m => m.idMensaje === tempMsg.idMensaje ? result.data : m);
          });
        }

        if (syncTicketAssignmentAfterMessage) {
          await syncTicketAssignmentAfterMessage();
        }

        if (setTicketDetail) {
          setTicketDetail(prev => prev ? { ...prev, dUltimaActividad: new Date().toISOString() } : prev);
        }

        if (onTicketUpdated) onTicketUpdated();

        window.dispatchEvent(new CustomEvent('akasia:support:ticket_activity', {
          detail: {
            idTicket: parseInt(activeTicketId),
            vTextoMensaje: messageText,
            dUltimaActividad: new Date().toISOString(),
            bEsAgente: 1
          }
        }));

        window.dispatchEvent(new CustomEvent('akasia:support:ticket_read', {
          detail: { idTicket: parseInt(activeTicketId) }
        }));
      }
    } catch (err) {
      if (showErrorToast) showErrorToast('Error de red al enviar mensaje: ' + err.message);
      setMessages(prev => prev.filter(m => m.idMensaje !== tempMsg.idMensaje));
    } finally {
      setIsSending(false);
    }
  }, [
    activeTicketId,
    ticketPhone,
    token,
    idUsuario,
    nombreCompleto,
    inputText,
    setInputText,
    selectedFile,
    setSelectedFile,
    filePreview,
    setFilePreview,
    isSending,
    syncTicketAssignmentAfterMessage,
    setTicketDetail,
    onTicketUpdated,
    showErrorToast,
    replyingTo
  ]);

  return {
    messages,
    setMessages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    isSending,
    replyingTo,
    setReplyingTo,
    handleLoadMore,
    handleSendMessage
  };
};
